import * as React from 'react';
import { Button, ColumnProps, Justify, Table } from '@rtkwlf/ui';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../../../widgets/Icons';
import {
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { OBAddress } from '../../../types';
import { useBooleanState, useEditAddModal } from '../../../hooks';
import OnboardingSiteModal from './OnboardingSiteModal';
import { apiRequest } from '../../../APIClient';
import { sessionSelector } from '../../../reducers/session';
import { useSortTableName } from '../hooks/useSortTableName';
import { OnboardingModal } from '../OnboardingModal';

const addressSummary = (address: OBAddress) => {
  const summary = [];
  if (address.street) {
    summary.push(address.street);
  }
  if (address.city) {
    summary.push(address.city);
  }
  if (address.region) {
    summary.push(address.region);
  }
  if (address.country) {
    summary.push(address.country);
  }
  return summary.join(', ');
};

/**
 * a hook to manage the states of the add/edit modal
 * it is a wrapper around the generic useEditAddModal
 */
const useSiteAddEditModal = () => {
  const {
    /* internally used by hook only */
    modalOpen,
    setModalOpen,

    /* pass to consumer */
    currentId,
    onEdit,
    onAdd,
  } = useEditAddModal();

  const onClose = React.useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const modal = (
    <OnboardingSiteModal
      isOpen={modalOpen}
      onClose={onClose}
      currentId={currentId}
    />
  );

  return {
    modal,
    onEdit,
    onAdd,
  };
};

const useSiteDeleteModal = (currentId: string, siteName: string) => {
  const dispatch = useDispatch();

  const siteReferencesSelector = React.useMemo(
    () => memoizedOnboardingSelectors.getSiteReferences(currentId),
    [currentId],
  );
  const siteReferences = useSelector(siteReferencesSelector);

  const [modalOpen, setModalOpen, openModal, closeModal] =
    useBooleanState(false);

  const onOk = React.useCallback(() => {
    setModalOpen(false);
    dispatch(apiRequest.deleteOBSite(currentId));
  }, [setModalOpen, dispatch, currentId]);

  const modal = (
    <OnboardingModal
      data-cy="site-delete-modal"
      title="Delete Site"
      visible={modalOpen}
      onCancel={closeModal}
      onOk={onOk}
      okDisabled={siteReferences.length > 0}
      okTitle="Yes, delete site"
    >
      {siteReferences.length > 0 ? (
        <p>
          Cannot delete <b>{siteName}</b>. It still is used by
          <ul>
            {siteReferences.map((ref) => (
              <li key={ref}>{ref}</li>
            ))}
          </ul>
        </p>
      ) : (
        <p>
          Are you sure you want to delete <b>{siteName}</b>?
        </p>
      )}
    </OnboardingModal>
  );

  return {
    modal,
    openModal,
  };
};

type TableData = {
  key: string;
  name: string;
  address: string;
  timezone: string;
  firewalls: string;
  finalized: boolean | undefined;
};

export const SiteCompleteButton = () => {
  const [isLoading, , setIsLoading, setIsNotLoading] = useBooleanState();
  const sites = useSelector(onboardingSelector.getSites);
  const completed = useSelector(onboardingSelector.getSitesCompleted);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const dispatch = useDispatch();

  const onCompleteSection = React.useCallback(() => {
    setIsLoading();
    dispatch(
      apiRequest.toggleOBSitesComplete({
        onError: setIsNotLoading,
        onSuccess: setIsNotLoading,
      }),
    );
  }, [dispatch, setIsLoading, setIsNotLoading]);
  const disabled =
    !canEdit || (Object.values(sites).length === 0 && !completed);

  return (
    <div className="ob-mark-complete-button">
      <Button
        type="primary"
        ghost={completed}
        onClick={onCompleteSection}
        disabled={disabled || isLoading}
        data-cy="site-complete-button"
      >
        {completed ? 'Mark Incomplete' : 'Mark Complete'}
      </Button>
    </div>
  );
};

const EditButton: React.FunctionComponent<{
  siteId: string;
  onEdit: (siteId: string) => void;
}> = ({ siteId, onEdit }) => {
  const onClick = React.useCallback(() => {
    onEdit(siteId);
  }, [onEdit, siteId]);

  return (
    <Icon.Pencil
      data-cy={`edit-site-${siteId}`}
      className="mr-2"
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    />
  );
};

const DeleteButton: React.FunctionComponent<{
  siteId: string;
  setCurrentId: (siteId: string) => void;
  openModal: () => void;
}> = ({ siteId, setCurrentId, openModal }) => {
  const onClick = React.useCallback(() => {
    setCurrentId(siteId);
    openModal();
  }, [setCurrentId, siteId, openModal]);

  return (
    <Icon.X
      data-id={siteId}
      data-cy={`delete-site-${siteId}`}
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    />
  );
};

const OnboardingSiteList = () => {
  const sites = useSelector(onboardingSelector.getSites);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (Object.keys(sites).length === 0) {
      dispatch(apiRequest.getOBSites());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const { modal: addEditModal, onEdit, onAdd } = useSiteAddEditModal();

  const [currentId, setCurrentId] = React.useState('');

  const { modal: deleteSiteModal, openModal: openDeleteSiteModal } =
    useSiteDeleteModal(currentId, sites[currentId]?.name ?? '');

  const { sort, handleSort } = useSortTableName<TableData>();
  const formattedSites: TableData[] = React.useMemo(() => {
    return sort(
      Object.keys(sites).map((id) => {
        const { name, address, timezone, firewalls, finalized } = sites[id];
        return {
          key: id,
          name,
          address: addressSummary(address),
          timezone,
          firewalls: firewalls.join(', '),
          finalized: !!finalized?.timestamp,
        };
      }),
    );
  }, [sites, sort]);

  const columns: ColumnProps<TableData>[] = React.useMemo(() => {
    const result: ColumnProps<TableData>[] = [
      {
        key: 'name',
        dataIndex: 'name',
        title: 'Name',
        sortable: true,
      },
      {
        key: 'address',
        dataIndex: 'address',
        title: 'Address',
      },
      {
        key: 'timezone',
        dataIndex: 'timezone',
        title: 'Timezone',
      },
      {
        key: 'firewalls',
        dataIndex: 'firewalls',
        title: 'Firewalls',
      },
    ];
    if (canEdit) {
      result.push({
        key: 'actions',
        title: 'Actions',
        width: 5,
        render: ({ record: { finalized, key } }) => {
          if (!finalized) {
            return (
              <>
                <EditButton siteId={key} onEdit={onEdit} />
                <DeleteButton
                  siteId={key}
                  setCurrentId={setCurrentId}
                  openModal={openDeleteSiteModal}
                />
              </>
            );
          } else {
            return <></>;
          }
        },
        justify: 'flex-end' as Justify,
      });
    }
    return result;
  }, [canEdit, onEdit, openDeleteSiteModal]);

  return (
    <>
      {canEdit && (
        <div className="pb-2 text-right">
          <Button
            data-cy="add-site-button"
            type="primary"
            ghost
            className="float-right mb-2"
            onClick={onAdd}
          >
            <Icon.Plus className="mr-2" />
            Add Site
          </Button>
        </div>
      )}

      <Table
        data={formattedSites}
        dataUniqueKey="value"
        columns={columns}
        onSort={handleSort}
        className="siteTable" // data-cy doesn't work here
        emptyComponent={
          canEdit && (
            <div
              className="my-5 text-center"
              data-cy="add-site-table-placeholder"
            >
              <span>
                Select
                <Button
                  type="link"
                  className="d-inline px-1 font-normal"
                  onClick={onAdd}
                >
                  Add Site
                </Button>
                to add a site location
              </span>
            </div>
          )
        }
      />
      {deleteSiteModal}
      {addEditModal}
    </>
  );
};

export default OnboardingSiteList;
